import React, { useRef, useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSystemSettings } from "../lib/systemSettings";

/**
 * MediaUploadCard
 *
 * Vereinheitlichte Upload-Card für Bildfelder im Admin-CMS
 * (Willkommens-Popup, Kampagnen-Popup, News). Optisch angeglichen an
 * ``PosAdBannerSlot`` in ``Einstellungen.jsx``:
 *   * Preview-Fläche mit dezenter Border, kein zu dunkler Frame.
 *   * Moderne gestrichelte Dropzone (Klick + Drag&Drop) im Empty-State.
 *   * Angezeigter Hinweis auf die empfohlene Bildgröße.
 *   * Zwei separate Render-Pfade Light/Dark auf Basis
 *     ``useSystemSettings().theme``.
 *
 * Uploads werden über ``POST /api/admin/portal/upload`` an das Portal-CMS-
 * Endpoint gesendet und liefern ``{ url }`` zurück. Der Aufrufer setzt
 * seinen Form-State über ``onChange(url)``.
 */
export function MediaUploadCard({
  label,
  value,
  onChange,
  testIdPrefix,
  recommendedSize,       // z. B. "1600 × 900 px (16:9)"
  helperText,            // z. B. "JPG · PNG · WebP · max. 3 MB"
  previewAspect = "video", // "video" (16:9), "wide" (7:1), "square" (1:1), oder "auto"
  accept = "image/jpeg,image/png,image/webp",
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const { theme } = useSystemSettings();
  const isLight =
    theme === "light" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: light)").matches);

  async function handleUpload(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/admin/portal/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(r.data.url);
      toast.success("Bild hochgeladen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleUpload(f);
  }
  function onDragOver(e) { e.preventDefault(); setDragOver(true); }
  function onDragLeave() { setDragOver(false); }

  // Preview-Höhen — bewusst pro Aspect-Ratio, damit das Motiv weder
  // gequetscht noch übertrieben groß wirkt.
  const aspectClass =
    previewAspect === "wide" ? "h-24 sm:h-32 lg:h-40" :
    previewAspect === "square" ? "h-40 sm:h-48 lg:h-56 aspect-square" :
    previewAspect === "auto" ? "min-h-[10rem]" :
    "aspect-video";  // default 16:9

  const hiddenInput = (
    <input
      ref={inputRef}
      data-testid={`${testIdPrefix}-file`}
      type="file"
      accept={accept}
      className="hidden"
      onChange={(e) => handleUpload(e.target.files?.[0])}
    />
  );

  // ---------------- LIGHT THEME ----------------
  if (isLight) {
    return (
      <div
        className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-5 shadow-sm space-y-4 transition-shadow hover:shadow-md"
        data-testid={`${testIdPrefix}-card`}
      >
        {label && (
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-zinc-900">{label}</div>
            <div
              className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                value ? "text-cyan-700 bg-cyan-50 border-cyan-200" : "text-zinc-500 bg-zinc-50 border-zinc-200"
              }`}
              data-testid={`${testIdPrefix}-status`}
            >
              {value ? "Bild hinterlegt" : "Kein Bild"}
            </div>
          </div>
        )}

        {value ? (
          <div
            className={`rounded-md overflow-hidden border border-zinc-200 bg-zinc-50 ${aspectClass}`}
            data-testid={`${testIdPrefix}-preview`}
          >
            <img src={value} alt="" draggable={false} className="w-full h-full object-cover object-center" />
          </div>
        ) : (
          <button
            type="button"
            data-testid={`${testIdPrefix}-preview`}
            onClick={() => inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            disabled={uploading}
            className={`w-full rounded-md border-2 border-dashed flex flex-col items-center justify-center gap-2 ${aspectClass} transition-colors cursor-pointer group ${
              dragOver ? "border-cyan-500 bg-cyan-50" : "border-zinc-300 bg-zinc-50 hover:border-cyan-400 hover:bg-cyan-50/40"
            }`}
          >
            <div
              className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                dragOver ? "bg-cyan-100 text-cyan-700" : "bg-white text-zinc-500 group-hover:text-cyan-600 border border-zinc-200"
              }`}
            >
              <Upload size={18} />
            </div>
            <div className="text-sm font-medium text-zinc-800">
              {uploading ? "Lade hoch…" : "Bild hierher ziehen oder klicken"}
            </div>
            {recommendedSize && (
              <div className="text-[11px] text-zinc-500">
                Optimale Bildgröße: <span className="font-mono text-zinc-700">{recommendedSize}</span>
              </div>
            )}
            {helperText && <div className="text-[11px] text-zinc-500">{helperText}</div>}
          </button>
        )}

        {hiddenInput}

        <div className="flex flex-wrap gap-2 items-center pt-1">
          <Button
            type="button"
            data-testid={`${testIdPrefix}-upload-btn`}
            className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 font-semibold shadow-sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            <Upload size={14} className="mr-2" />
            {uploading ? "Lade hoch…" : (value ? "Motiv ersetzen" : "Bild hochladen")}
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              data-testid={`${testIdPrefix}-remove-btn`}
              onClick={() => onChange?.("")}
            >
              <Trash2 size={14} className="mr-2" /> Entfernen
            </Button>
          )}
        </div>
        {value && recommendedSize && (
          <div className="text-[11px] text-zinc-500">
            Optimale Bildgröße: <span className="font-mono text-zinc-700">{recommendedSize}</span>
            {helperText && <span className="text-zinc-400"> · {helperText}</span>}
          </div>
        )}
      </div>
    );
  }

  // ---------------- DARK THEME (Referenz: PosAdBannerSlot) ----------------
  return (
    <div
      className="border border-zinc-800 rounded-sm bg-zinc-950/60 p-3 space-y-3"
      data-testid={`${testIdPrefix}-card`}
    >
      {label && (
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-zinc-400">{label}</div>
          <div
            className={`text-[11px] uppercase tracking-widest ${value ? "text-cyan-400" : "text-zinc-500"}`}
            data-testid={`${testIdPrefix}-status`}
          >
            {value ? "Bild hinterlegt" : "Kein Bild"}
          </div>
        </div>
      )}

      {value ? (
        <div
          className={`rounded-sm overflow-hidden border border-zinc-800 bg-zinc-950 ${aspectClass}`}
          data-testid={`${testIdPrefix}-preview`}
        >
          <img src={value} alt="" draggable={false} className="w-full h-full object-cover object-center" />
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testIdPrefix}-preview`}
          onClick={() => inputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          disabled={uploading}
          className={`w-full rounded-sm border-2 border-dashed flex flex-col items-center justify-center gap-2 ${aspectClass} transition-colors cursor-pointer group ${
            dragOver ? "border-cyan-400 bg-cyan-500/10" : "border-zinc-700 bg-zinc-950 hover:border-cyan-500/60 hover:bg-zinc-900/60"
          }`}
        >
          <div
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              dragOver ? "bg-cyan-500/20 text-cyan-300" : "bg-zinc-900 text-zinc-500 group-hover:text-cyan-400 border border-zinc-800"
            }`}
          >
            <Upload size={18} />
          </div>
          <div className="text-sm font-medium text-zinc-200">
            {uploading ? "Lade hoch…" : "Bild hierher ziehen oder klicken"}
          </div>
          {recommendedSize && (
            <div className="text-[11px] text-zinc-500">
              Optimale Bildgröße: <span className="font-mono text-zinc-300">{recommendedSize}</span>
            </div>
          )}
          {helperText && <div className="text-[11px] text-zinc-500">{helperText}</div>}
        </button>
      )}

      {hiddenInput}

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          type="button"
          data-testid={`${testIdPrefix}-upload-btn`}
          className="bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-cyan-400"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          <Upload size={14} className="mr-2" />
          {uploading ? "Lade hoch…" : (value ? "Motiv ersetzen" : "Bild hochladen")}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            className="text-rose-400"
            data-testid={`${testIdPrefix}-remove-btn`}
            onClick={() => onChange?.("")}
          >
            <Trash2 size={14} className="mr-2" /> Entfernen
          </Button>
        )}
      </div>
      {value && recommendedSize && (
        <div className="text-[11px] text-zinc-500">
          Optimale Bildgröße: <span className="font-mono text-zinc-300">{recommendedSize}</span>
          {helperText && <span className="text-zinc-600"> · {helperText}</span>}
        </div>
      )}
    </div>
  );
}
